import { createRoot } from 'react-dom/client';
import * as O from 'fp-ts/lib/Option';
import jwt from 'jsonwebtoken';
import { toast } from 'react-toastify';
import { modules } from 'src/constants';
import { getTokens, useLocalStorage } from '@schemeserve/global-utils';
import xhr from 'xhr';
import { provideReduxToModules } from './provideReduxToModules';
import { renderConditions, inIframe, createFeauterAwareRenderDecisions, createFeatureAwareRenderDecisions } from './renderConditions';
import { containerCreators } from './containerCreators';
import { createTheme } from './createTheme';
import { formatUserPermissions } from './permissions';
import createFeatureDecisions, { Flags } from 'src/services/feauterDecisions';
import jwtDecode from 'jwt-decode';
import { reactModulesMap } from 'src/data/modules';
import { pipe } from 'fp-ts/lib/function';

async function getStore() {
  if (!inIframe()) {
    const store = await import('../store');
    return store.default;
  }
  return undefined;
}

/**
 * Keep a record of the latest module that has been clicked.
 * Used for error handling, so that we automatically know which
 * module has caused an error.
 *
 * @category Initialization
 * @subcategory Init React Apps
 * @module setOnModuleClick
 */
const setOnModuleClick = () => {
  document.body.addEventListener('click', (e) => {
    const closestModule = e.target.closest('.react-module');

    if (closestModule && Object.keys(modules).includes(closestModule.id)) {
      window.lastClickedReactModule = closestModule.id;
    }
  });
};

/**
 * Development only -
 * Renders the modules in their development containers (dynamically created)
 *
 * @category Initialization
 * @subcategory Init React Apps
 * @module renderInDevelopmentContainers
 * @param {object} moduleData
 */
const renderInDevelopmentContainers = (moduleData) => {
  if (document.getElementById(moduleData.ref)) {
    const container = document.getElementById(moduleData.ref);
    const root = createRoot(container); // createRoot(container!) if you use TypeScript
    root.render(moduleData.module);
  }
};

/**
 * Wrap the actual component property in necessary HOC's
 * Check if module should render on current page
 * Create it's container div - render it
 * @category Initialization
 * @subcategory Init React Apps
 * @module executeModuleRender
 */
const executeModuleRender = async (modules) => {
  const wrappedModules = provideReduxToModules(modules);
  wrappedModules.forEach((moduleData) => {
    if (process.env.NODE_ENV === 'development') {
      // dev relies on the container already existing in index.html
      // so conditionally render the module only if container exists
      renderInDevelopmentContainers(moduleData);
      return;
    }

    const containerCreator = containerCreators[moduleData.ref];

    containerCreator.create();
    try {
      const container = document.getElementById(containerCreator.root);
      const root = createRoot(container);
      root.render(moduleData.module);
    } catch (error) {
      console.error(`Problem rendering ${moduleData.ref}: ${error}`);
    }
  });
};

/**
 * Set the core global state.
 * Define any breaking errors at current stage.
 * Continue even with breaking errors and handle them later.
 * @category Initialization
 * @subcategory Init React Apps
 * @module setCoreState
 */
const setCoreState = ({ token, siteData = {} }, store) => {
  window.apiUrl =
    window.ENVIRONMENT &&
    window.ENVIRONMENT.ApiRoot &&
    window.ENVIRONMENT.ApiRoot.Base;
  window.devRoutes = token.Routes ? JSON.parse(token.Routes) : {};

  store.dispatch({
    type: 'SET_CORE_DATA',
    payload: {
      breakingErrors: {
        token: !window.isLoggedIn ? false : !Object.keys(token).length,
        apiUrl: !window.apiUrl,
      },
      site: {
        ...siteData,
        siteUrl:
          window.ENVIRONMENT &&
          window.ENVIRONMENT.SecureRoot.replace(/\/$/, ''),
      },
      featureFlags: formatUserPermissions(token),
      user: {
        ...token,
        accessGroup: token.accessGroup || window.User.AccessGroup,
        isAgent:
          process.env.NODE_ENV === 'development'
            ? window.User.IsAgent
            : token.AccessGroup === 'AgentAdministrators',
        isMasterUser:
          process.env.NODE_ENV === 'development'
            ? window.User.IsMasterUser
            : token.AccessGroup === 'AdmSuperUsers',
        isClient:
          process.env.NODE_ENV === 'development'
            ? window.User.IsClient
            : token.AccessGroup === 'Clients',
        siteUrl:
          window.ENVIRONMENT &&
          window.ENVIRONMENT.SecureRoot.replace(/\/$/, ''),
      },
      config: {
        apiUrls: window.ENVIRONMENT && window.ENVIRONMENT.ApiRoot,
        apiUrl: window.ENVIRONMENT && window.ENVIRONMENT.ApiRoot.Base,
        // devRoutes: JSON.parse(jwtData.Routes) || {},
      },
    },
  });
};

/**
 * Wait until the dom has loaded
 * @category Initialization
 * @subcategory Init React Apps
 * @module initReactApps
 */
export const initReactApps = async (modulesLocal, featureFlags) => {
  let stackGuard = 1;
  const store = await getStore();

  const token = pipe(
    getTokens(),
    O.map((tokens) => jwtDecode(tokens.accessToken)),
    O.getOrElse(() => ({})),
  );

  const featureDecisions = createFeatureDecisions(
    featureFlags,
    formatUserPermissions(token),
  );
  const renderDecisions = createFeatureAwareRenderDecisions(featureDecisions);
  let moduelsToDisplay = modulesLocal.filter(renderDecisions);

  /*
  if (process.env.NODE_ENV === 'development') {
    moduelsToDisplay = modulesLocal;
  }
  */

  // TODO remove this once QSE is fully LIVE and we want to get rid of old FE
  if (
    window.location.href
      .toLowerCase()
      .indexOf('schemeserve/admin/questions/edit.aspx') !== -1 &&
    featureDecisions.qseShouldUseTestMode()
  ) {
    const OLDUI = 'oldUi';
    const search = new URLSearchParams(window.location.search);
    if (!search.has(OLDUI)) {
      search.append(OLDUI, 'true');
      window.location.href = `${window.location.origin}${window.location.pathname}?${search.toString()}`;
    }
  }

  const interval = setInterval(() => {
    stackGuard += 1;

    if (stackGuard > 100) {
      clearInterval(interval);
    }
    const isDocumentRendered = !!document.body;

    let siteData;

    if (useLocalStorage().hasKey('ss_site_data')) {
      siteData = useLocalStorage().getItem('ss_site_data');
    }

    if (isDocumentRendered) {
      clearInterval(interval);
      createTheme(siteData);
      setCoreState(
        {
          token,
          siteData,
        },
        store,
      );
      setOnModuleClick();
      executeModuleRender(moduelsToDisplay);
    }
  }, 100);
};

export default initReactApps;
